/**
 * Peer Review tab — shadow / human reviewer comparison for a disclosure case.
 *
 * Reviewer pastes notes (and/or uploads .pdf/.md/.txt files); Clara compares
 * those against the case's current readiness verdict and rule assessment and
 * returns a structured comparison: agreement, divergences, learnings.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faFile,
  faXmark,
  faTriangleExclamation,
  faCircleCheck,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import clsx from "clsx";

import {
  listPeerReviewRuns,
  runPeerReview,
  updatePeerReviewNotes,
  type PeerAgreement,
  type PeerReviewRun,
  type PeerReviewDivergence,
  type PeerWhoIsRight,
  type DisclosureGrade,
} from "@/services/api/disclosures";

const ACCEPT = ".pdf,.md,.txt";
const ALLOWED_EXTS = new Set(["pdf", "md", "txt"]);
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const AGREEMENT_TONE: Record<PeerAgreement, string> = {
  agree: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  partial: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  disagree: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};

const WHO_TONE: Record<PeerWhoIsRight, string> = {
  agent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  human: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  both: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  unclear: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
};

const GRADE_TONE: Record<string, string> = {
  "ready-to-file": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "minor-issues": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "needs-investigation": "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  "blocking-gap": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "do-not-file": "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

interface PeerReviewTabProps {
  caseId: string;
  caseTitle: string;
}

export function PeerReviewTab({ caseId, caseTitle }: PeerReviewTabProps) {
  const [runs, setRuns] = useState<PeerReviewRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pastedText, setPastedText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [showInputs, setShowInputs] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listPeerReviewRuns(caseId, 20);
      setRuns(list);
      if (list.length > 0) {
        setActiveRunId((prev) => prev ?? list[0].id);
        setShowInputs(false);
      } else {
        setShowInputs(true);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load peer review runs");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    setActiveRunId(null);
    setRuns([]);
    setPastedText("");
    setFiles([]);
    refresh();
  }, [refresh]);

  const onFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    const accepted: File[] = [];
    const rejected: string[] = [];
    for (const f of picked) {
      const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
      if (!ALLOWED_EXTS.has(ext)) {
        rejected.push(`${f.name}: unsupported (allowed: .pdf, .md, .txt)`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`${f.name}: exceeds 25MB`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length > 0) {
      setFiles((prev) => {
        const seen = new Set(prev.map((p) => `${p.name}:${p.size}`));
        return [...prev, ...accepted.filter((f) => !seen.has(`${f.name}:${f.size}`))];
      });
    }
    setError(rejected.length > 0 ? rejected.join("; ") : null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const hasInput = pastedText.trim().length > 0 || files.length > 0;

  const onRun = useCallback(async () => {
    if (!hasInput) return;
    setRunning(true);
    setError(null);
    try {
      const run = await runPeerReview(caseId, {
        humanReviewText: pastedText.trim() || undefined,
        files,
      });
      setRuns((prev) => [run, ...prev]);
      setActiveRunId(run.id);
      setPastedText("");
      setFiles([]);
      setShowInputs(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(detail || e?.message || "Peer review failed");
    } finally {
      setRunning(false);
    }
  }, [caseId, files, hasInput, pastedText]);

  const activeRun = runs.find((r) => r.id === activeRunId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900 dark:text-white">
          Shadow / peer review
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Compare Clara&apos;s readiness verdict for{" "}
          <span className="font-medium">{caseTitle}</span> against a human peer reviewer&apos;s
          notes. Paste their write-up below, attach files, and run the comparison.
        </p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-sm flex items-start gap-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700">
            ✕
          </button>
        </div>
      )}

      {loading && runs.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
        </div>
      )}

      {/* Saved runs picker */}
      {runs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {runs.map((r) => (
            <button
              key={r.id}
              onClick={() => setActiveRunId(r.id)}
              className={clsx(
                "px-3 py-1.5 text-xs rounded-lg border transition-colors",
                r.id === activeRunId
                  ? "border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"
                  : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800",
              )}
            >
              <span className="font-medium">
                {new Date(r.created_at).toLocaleString()}
              </span>
              <span className="ml-2 text-slate-400">
                ({r.input_manifest.files.length} files,{" "}
                {r.input_manifest.human_review_text_char_count} chars)
              </span>
            </button>
          ))}
          <button
            onClick={() => setShowInputs((v) => !v)}
            className="px-3 py-1.5 text-xs rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {showInputs ? "Hide inputs" : "+ New comparison"}
          </button>
        </div>
      )}

      {/* Inputs */}
      {(showInputs || runs.length === 0) && (
        <section className="bg-white dark:bg-white/[0.03] rounded-xl border border-slate-200 dark:border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={onFilePick}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={running}
              className="px-3 py-1.5 text-sm rounded-lg bg-slate-100 dark:bg-white/5 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-white/10 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faFile} /> Add files (.pdf / .md / .txt)
            </button>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Up to 25MB per file. Paste notes below in addition to or instead of files.
            </span>
          </div>

          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${f.size}-${i}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/[0.03] text-sm"
                >
                  <FontAwesomeIcon icon={faFile} className="text-slate-400" />
                  <span className="flex-1 truncate text-slate-800 dark:text-slate-100">
                    {f.name}
                  </span>
                  <span className="text-xs text-slate-500">{fmtBytes(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    disabled={running}
                    className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-xs" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Paste the peer reviewer's notes here…"
            disabled={running}
            className="w-full min-h-[140px] rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50"
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onRun}
              disabled={running || !hasInput}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-sky-500 via-blue-500 to-cyan-500 hover:opacity-90 text-white disabled:opacity-50 inline-flex items-center gap-2 shadow-sm transition-opacity"
            >
              {running && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
              {running ? "Comparing…" : activeRun ? "Run new comparison" : "Compare"}
            </button>
          </div>
        </section>
      )}

      {/* Active run detail */}
      {activeRun && (
        <PeerReviewRunCard
          caseId={caseId}
          run={activeRun}
          savingNotes={savingNotes}
          onNotesChange={async (notes) => {
            setSavingNotes(true);
            try {
              const updated = await updatePeerReviewNotes(caseId, activeRun.id, notes);
              setRuns((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
            } catch (e: any) {
              setError(e?.message || "Failed to save notes");
            } finally {
              setSavingNotes(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ── Run card ─────────────────────────────────────────────────────────

function PeerReviewRunCard({
  run,
  savingNotes,
  onNotesChange,
}: {
  caseId: string;
  run: PeerReviewRun;
  savingNotes: boolean;
  onNotesChange: (notes: string) => Promise<void>;
}) {
  const [draftNotes, setDraftNotes] = useState(run.notes);

  useEffect(() => {
    setDraftNotes(run.notes);
  }, [run.id, run.notes]);

  const c = run.comparison;
  const dirty = draftNotes !== run.notes;

  return (
    <section className="bg-white dark:bg-white/[0.03] rounded-xl border border-slate-200 dark:border-white/10 p-4 space-y-4">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Comparison
          </div>
          <div className="text-base font-semibold text-slate-900 dark:text-white">
            {c.case_title} · {c.case_type_label}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Saved {new Date(run.created_at).toLocaleString()}
          </div>
        </div>
        <span
          className={clsx(
            "px-3 py-1.5 rounded-full text-sm font-semibold capitalize",
            AGREEMENT_TONE[c.agreement],
          )}
        >
          {c.agreement}
        </span>
      </header>

      {/* Grade comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <GradePanel label="Clara (agent)" grade={c.agent_grade ?? null} />
        <GradePanel label="Human peer reviewer" grade={c.human_grade ?? null} />
      </div>

      {/* Summary */}
      {c.summary && (
        <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
          {c.summary}
        </p>
      )}

      {/* Divergences */}
      {c.divergences.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
          <FontAwesomeIcon icon={faCircleCheck} />
          No divergences flagged — reviewer is aligned with Clara.
        </div>
      ) : (
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Divergences ({c.divergences.length})
          </div>
          <ul className="space-y-3">
            {c.divergences.map((d, i) => (
              <DivergenceRow key={i} d={d} />
            ))}
          </ul>
        </div>
      )}

      {/* Learnings */}
      {c.learnings.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
            Learnings
          </div>
          <ul className="text-sm text-slate-700 dark:text-slate-300 list-disc pl-5 space-y-1">
            {c.learnings.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Compliance notes (free-form, persisted) */}
      <div>
        <div className="text-sm font-semibold text-slate-900 dark:text-white mb-2">
          Compliance notes
        </div>
        <textarea
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
          placeholder="Reviewer's final notes — accept Clara's verdict, add escalation, etc."
          className="w-full min-h-[100px] rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
        />
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            disabled={!dirty || savingNotes}
            onClick={() => onNotesChange(draftNotes)}
            className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {savingNotes && <FontAwesomeIcon icon={faSpinner} className="animate-spin" />}
            Save notes
          </button>
          {!dirty && run.notes && (
            <span className="text-xs text-slate-400">Saved.</span>
          )}
        </div>
      </div>
    </section>
  );
}

function GradePanel({
  label,
  grade,
}: {
  label: string;
  grade: NonNullable<DisclosureGrade> | null;
}) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1.5">
        {grade ? (
          <span
            className={clsx(
              "px-2 py-1 rounded-full text-sm font-semibold",
              GRADE_TONE[grade] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
            )}
          >
            {grade}
          </span>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
      </div>
    </div>
  );
}

function DivergenceRow({ d }: { d: PeerReviewDivergence }) {
  return (
    <li className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="font-medium text-sm text-slate-900 dark:text-white">{d.topic}</div>
        <span
          className={clsx(
            "px-2 py-0.5 rounded-full text-xs font-semibold",
            WHO_TONE[d.who_is_right],
          )}
        >
          {d.who_is_right === "agent" && "Clara correct"}
          {d.who_is_right === "human" && "Human correct"}
          {d.who_is_right === "both" && "Both have a point"}
          {d.who_is_right === "unclear" && "Unclear"}
        </span>
      </div>
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <div className="rounded border border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-950/30 p-2">
          <div className="font-semibold text-blue-700 dark:text-blue-300 mb-0.5">
            Clara
          </div>
          <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
            {d.agent_view || "—"}
          </div>
        </div>
        <div className="rounded border border-violet-100 dark:border-violet-900/40 bg-violet-50 dark:bg-violet-950/30 p-2">
          <div className="font-semibold text-violet-700 dark:text-violet-300 mb-0.5">
            Human reviewer
          </div>
          <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
            {d.human_view || "—"}
          </div>
        </div>
      </div>
      {d.explanation && (
        <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 italic">
          {d.explanation}
        </div>
      )}
      {d.rule_reference && (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500 font-mono">
          ref: {d.rule_reference}
        </div>
      )}
    </li>
  );
}

export default PeerReviewTab;
